import { Controller } from "@hotwired/stimulus"

/**
 * Access Gate Controller — intercepts writes when the user's subscription is inactive.
 *
 * When attached to a button/link with `click->access-gate#block`, it cancels the
 * original event (stops link navigation, form submit, and any other Stimulus actions
 * on the same element) and shows a toast explaining the subscription is not active.
 *
 * Add `data-access-gate-message-value="..."` to customize the toast message.
 */
export default class extends Controller {
  static values = { message: String }

  block(event) {
    event.preventDefault()
    event.stopImmediatePropagation()

    // Optional: haptic feedback via the existing native bridge, if present.
    const haptic = document.querySelector("[data-controller~='bridge--haptic']")
    if (haptic) {
      haptic.dispatchEvent(new CustomEvent("haptic:notify", { detail: { style: "warning" } }))
    }

    this.showToast()
  }

  showToast() {
    const container = document.getElementById("toast-notifications")
    if (!container) return

    // Dedupe: clear any existing access-gate toasts so rapid taps don't stack.
    container.querySelectorAll("[data-access-gate-toast]").forEach(n => n.remove())

    const message = this.hasMessageValue
      ? this.messageValue
      : "Your subscription is not active."

    const wrapper = document.createElement("div")
    wrapper.className = "pointer-events-auto"
    wrapper.setAttribute("data-access-gate-toast", "")
    wrapper.innerHTML = this.toastHTML(message)
    container.appendChild(wrapper)
  }

  toastHTML(message) {
    const safe = message
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")

    return `
      <div data-toast-manager-target="toast"
           data-action="click->toast-manager#dismiss"
           role="alert"
           class="inline-flex items-center gap-2.5 px-4 py-3
                  bg-base-100 rounded-full
                  shadow-[0_8px_32px_rgba(15,23,42,0.12),0_2px_8px_rgba(15,23,42,0.06)]
                  border border-base-300/80 cursor-pointer max-w-[90vw]">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="flex-shrink-0">
          <circle cx="12" cy="12" r="10" stroke="#EF4444" stroke-width="1.5" class="toast-icon-circle"/>
          <path d="M14.5 9.5l-5 5M9.5 9.5l5 5" stroke="#EF4444" stroke-width="1.5"
                stroke-linecap="round" class="toast-icon-x"/>
        </svg>
        <p class="text-[13px] text-base-content font-semibold leading-tight">${safe}</p>
      </div>
    `
  }
}
